<template lang="html">
  <tr>
    <td :colspan="showFees ? 7 : 6"
        class="operation__details">

      <template v-if="document.virtual">
        <div class="document-virtual--alert">
          <div>
            <inline-svg :src="require('./icons/documents-tree-dashed.svg')" width="37" height="37"/>
          </div>
          <span class="dv--alert-text">{{
              $t('Это виртуальный документ, который при исполнении разбивается на несколько платежей поменьше.')
            }}</span>
        </div>
      </template>

      <div class="document-details--grid">
        <div class="transaction-grid">
          <div class="item">
            <div class="key">{{ $t('Номер транзакции') }}</div>
            <div class="value">{{ document.docNumber }}</div>
          </div>
          <template v-if="!document.virtual">
            <div class="item">
              <div class="key">{{ $t('Статус') }}</div>
              <div class="value breakable">
              <span is="document-status-title" :document="document"
                    :title-executed="$t('Платеж исполнен.')"
                    :title-manual-process="$t('Платеж в обработке.')"
                    :title-declined="$t('Платеж отклонен.')"
                    :title-inprocess="$t('Платеж в обработке.')"
                    :title-virtual="$t('Виртуальный платеж.')"
              ></span>
              </div>
            </div>
          </template>



          <div class="item">
            <div class="key">{{ $t('Сумма операции') }}</div>
            <div class="value">
              {{ moneyFormat(document.actualAmount, document.actualCurrency) }}
              {{ frontofficeCurrencyFilter(document.actualCurrency, true) }}
            </div>
          </div>

          <template v-if="document?.comment">
            <div class="item">
              <div class="key">{{ $t(document.state == 'declined' ? 'Причина отклонения' : 'Комментарий') }}</div>
              <div class="value breakable">{{ String(document?.comment).trim() }}</div>
            </div>
          </template>

          <div class="item">
            <div class="key">{{ taxLabel }}</div>
            <div class="value">
              <template v-if="isNegativeTax">
                <nobr><strong>{{ moneyFormat(document.tax, document.currency) }}
                  {{ frontofficeCurrencyFilter(document.currency, true) }}</strong></nobr>
              </template>
              <template v-else>
                <nobr>{{ moneyFormat(document.tax, document.currency) }} {{
                    frontofficeCurrencyFilter(document.currency, true)
                  }}
                </nobr>
              </template>
            </div>
          </div>

          <div class="item" v-if="document.sender && !isCapitalistSystem">
            <div class="key">{{ $t('Отправитель') }}</div>
            <div class="value breakable">{{ document.sender }}</div>
          </div>
          <div class="item" v-if="!document.outgoing && isWireWithDetails">
            <div class="key">{{ $t('Корреспондент') }}</div>
            <div class="value breakable">{{ document.corrDetails.corrName }},
              {{ document.corrDetails.corrBankAccount }},
              {{ document.corrDetails.corrBankName }}
            </div>
          </div>

          <div class="item" v-if="document.description">
            <div class="key">{{ $t('Назначение платежа') }}</div>
            <div class="value breakable">{{ document.description ? collapseSpaces(document.description) : '&mdash;' }}</div>
          </div>
          <div class="item" v-if="document.outgoing || document.selfExchange">
            <div class="key">{{ $t('Способ верификации') }}</div>
            <div class="value">
              <span>{{ $t(document.verificationType) }}</span>
            </div>
          </div>

          <div class="item" v-if="isBatchRecord">
            <div class="key">{{ $t('Массовый платеж') }}</div>
            <div class="value">
              <span>{{ document.batch?.id }}</span>
            </div>
          </div>
          <div class="item" v-if="document.receiver && !isWireWithDetails && !isSepaWithDetails">
            <div class="key">{{ $t('Получатель') }}</div>
            <div class="value breakable">{{ document.receiver }}</div>
          </div>

          <div class="item" v-if="document.outgoing && isWireWithDetails">
            <div class="key">{{ $t('Получатель') }}</div>
            <div class="value breakable">
              {{ document.corrDetails.corrName }},
              {{ document.corrDetails.corrBankAccount }},
              {{ document.corrDetails.corrBankName }}
            </div>
          </div>

          <template v-if="isSepaWithDetails">
            <div class="item">
              <div class="key">{{ $t('Получатель') }}</div>
              <div class="value breakable">
                {{ document.meta?.beneficiaryName }}
              </div>
            </div>
            <div class="item">
              <div class="key">{{ $t('IBAN') }}</div>
              <div class="value breakable">
                {{ document.meta?.IBAN }}
              </div>
            </div>
            <div class="item">
              <div class="key">{{ $t('Банк') }}</div>
              <div class="value breakable">
                {{ document.meta?.bankName }}
              </div>
            </div>
          </template>

          <template v-if="document.tx">
            <div class="item">
              <div class="key">{{ $t('Транзакция') }}</div>
              <div class="value breakable">
                <CryptoTxUrlWithQrCode style="text-decoration: underline" :txid="document.tx.txId"
                                       :url="document.tx.txUrl"/>
              </div>
            </div>

          </template>
        </div>
      </div>


      <prepaid-document-details :document="document" v-if="isPrepaid"></prepaid-document-details>


      <template v-if="pdfAvailable">
        <br/>
        <DocumentDetailsPdfPanel :id="document.id"/>
      </template>
    </td>
  </tr>
</template>

<script lang="ts">
import Vue from 'vue';
import {Component, Prop} from 'vue-property-decorator';
import {formatUnixDateFilter} from '@/models/filters/datetime';
import {MoneyFormatFilter} from '@/models/filters/money';
import DocumentStatusTitle from '@/views/pages/finance/history/DocumentStatusTitle.vue';
import PrepaidDocumentDetails
  from "@/views/pages/finance/history/prepaid/PrepaidDocumentDetails.vue";
import InlineSvg from "vue-inline-svg";
import {frontofficeCurrencyFilter} from "@/models/core/currency";
import {
  historyDocumentPdfAvailable,
} from "capitalist-shared/src/finance/documents";
import DocumentDetailsPdfPanel from "@/views/pages/finance/history/DocumentDetailsPdfPanel.vue";
import CryptoTxUrlWithQrCode from "@/views/pages/finance/history/crypto/CryptoTxUrlWithQrCode.vue";

@Component({
  methods: {
    frontofficeCurrencyFilter,
    tsFormat: formatUnixDateFilter,
    moneyFormat: MoneyFormatFilter,
  },
  components: {
    CryptoTxUrlWithQrCode,
    DocumentDetailsPdfPanel, InlineSvg, PrepaidDocumentDetails, DocumentStatusTitle
  },
  name: 'document-details',
})
export default class DocumentDetails extends Vue {
  @Prop({required: true})
  document: any

  @Prop({default: false})
  showFees!: boolean

  get pdfAvailable() {
    return historyDocumentPdfAvailable(this.document);
  }

  collapseSpaces(str) {
    return str.replace(/\s+/g, ' ').trim();
  }

  get isNegativeTax() {
    return this.document.tax < 0
  }

  get isSepa(): boolean {
    return this.document.channelType == 'sepaifx';
  }

  get isSepaWithDetails(): boolean {
    return this.isSepa && this.document.meta;
  }

  get isBatchRecord(): boolean {
    return this.document.batch;
  }

  get isPrepaid() {
    return Boolean(this.document.prepaid)
  }

  get isWire(): boolean {
    return this.document.channelType == 'wire';
  }

  get isWireWithDetails(): boolean {
    return this.isWire && this.document.corrDetails;
  }

  get isCorrespondentNotEmpty(): boolean {
    return this.document.beneficiary && (this.document.beneficiary != this.document.channel);
  }

  get taxLabel() {
    return this.isNegativeTax ? this.$t('Кешбэк') : this.$t('Комиссия');
  }

  get isCapitalistSystem(): boolean {
    return this.document.beneficiary == 'Capitalist System';
  }
}
</script>

<style scoped lang="less">
.document-details--grid {
  .transaction-grid {
    margin: 0 auto;
    display: flex;
    flex-wrap: wrap;
    gap: 5px 4%;
  }

  .monospace {
    font-family: 'Roboto Mono', monospace;
  }

  .item {
    transition: background 0.3s;
    display: flex;
    width: 48%;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 0.1em;
    padding: 8px 8px 4px 8px;
    border-radius: 5px;

    &:hover {
      background: #EEEEEE;

      .key {
        color: #0a0a0a;
      }

      .value {

      }
    }
  }

  .key {
    transition: color 0.3s;
    line-height: 1em;
    width: auto;
    font-weight: 600;
    font-size: 0.9em;
    color: #999;
  }

  .value {
    //
    line-height: 1.4em;
    white-space: nowrap;
    color: #000;
    user-select: all;

    &.breakable {
      white-space: break-spaces;
      word-break: break-all;
    }
  }
}

.operation__details {

}

.document-virtual--alert {
  margin: 10px 30px 15px 0;
  border: 1px solid #f0ad4e;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 12px 15px;
  background-color: #fcf8e3;

  display: flex;
  flex-direction: row;
  gap: 15px;
  align-items: center;

  .dv--alert-text {
    line-height: 1.4em;
  }
}
</style>

<i18n>
{
  "en": {
    "Комментарий": "Comment",
    "Причина отклонения": "Rejection reason",
    "Платежный PIN-код": "Payment PIN-code",
    "Кешбэк": "Cashback",
    "Комиссия": "Commission",
    "Номер транзакции": "Transaction number",
    "Сумма операции": "Amount",
    "Отправитель": "Sender",
    "Корреспондент": "Correspondent",
    "Статус": "Status",
    "Платеж исполнен.": "Payment executed.",
    "Платеж отклонен.": "Payment rejected.",
    "Платеж в обработке.": "Payment in process.",
    "Виртуальный платеж.": "Virtual payment.",
    "Назначение платежа": "Payment description",
    "Способ верификации": "Verification method",
    "Получатель": "Beneficiary",
    "Транзакция": "Transaction",
    "Банк": "Bank",
    "Массовый платеж": "Mass payment",
    "Это виртуальный документ, который при исполнении разбивается на несколько платежей поменьше.": "This is a virtual document that is split into several smaller payments when executed."
  }
}
</i18n>
